/**
 * MonthlyReportPage - Top-level page component for the Health Diary Monthly Report.
 *
 * Triggers report generation on mount (or when params change), manages loading/error states,
 * and renders all report sections in order:
 *   Header → Executive Summary → Vitals Graphs → Output Analytics →
 *   Input Meal Matrix → Feeding Method Summary → Care Notes Timeline → Raw Event Appendix
 *
 * Uses React Error Boundary for section-level error isolation so one section
 * failing doesn't crash the whole report.
 *
 * Requirements: 17.1, 1.3, 1.4
 */

import React, { useState, useEffect, Component } from 'react';
import type { ErrorInfo, ReactNode } from 'react';
import type { MonthlyReportViewModel, ChartDataSeries } from '../types';
import type { VitalsTableRow } from './VitalsGraphs';
import type { CompanyInfo, PatientInfo } from './ReportHeader';
import { buildMonthlyReportViewModel } from '../viewmodel/viewModelBuilder';
import { fetchMonthlyJournals } from '../api/fetchMonthlyJournals';
import { extractCategories } from '../pipeline/categoryExtractor';
import { normalizeInputRecords, normalizeOutputRecords, normalizeNoteRecords } from '../pipeline/normalizer';
import { deduplicate } from '../pipeline/deduplicator';
import { aggregateInput } from '../aggregation/inputAggregator';
import { aggregateOutput } from '../aggregation/outputAggregator';
import { aggregateNotes } from '../aggregation/notesAggregator';
import { clusterVitalRecords } from '../clustering/vitalTimeClusterer';
import { readGlucoseValue } from '../api/fetchMonthlyJournals';
import type { BpReading, ClusterResult, GlucoseReading } from '../clustering/types';
import { formatReportTimeGroup } from '../clustering/circularTime';
import { CompanyHash, CenterHashCompanyList, getCustomerCenter, CentreList } from '../../../../constant';
import axios from 'axios';
import { API_DOMAIN, API_KEY } from '../../user_constant';
import QRCode from 'qrcode';
// @ts-ignore
import CryptoJS from 'crypto-js';

// Child components (will be created in subsequent tasks)
import ReportHeader from './ReportHeader';
import ExecutiveSummary from './ExecutiveSummary';
import VitalsGraphs from './VitalsGraphs';
import OutputAnalytics from './OutputAnalytics';
import InputMealMatrix from './InputMealMatrix';
import CareNotesTimeline from './CareNotesTimeline';
import RawEventAppendix from './RawEventAppendix';

// ─── Section Error Boundary ──────────────────────────────────────────────────

interface SectionErrorBoundaryProps {
  sectionName: string;
  hideTitle?: boolean;
  children?: ReactNode;
}

interface SectionErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

/**
 * Error boundary that wraps individual report sections.
 * If a section throws during render, it displays a fallback message
 * without crashing the entire report.
 */
class SectionErrorBoundary extends Component<SectionErrorBoundaryProps, SectionErrorBoundaryState> {
  constructor(props: SectionErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): SectionErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    console.error(
      `[MonthlyReport] Error in section "${this.props.sectionName}":`,
      error,
      errorInfo
    );
  }

  render(): ReactNode {
    if (this.state.hasError) {
      return (
        <div
          className="report-section-error"
          style={{
            padding: '1rem',
            margin: '0.5rem 0',
            border: '1px solid #f5c6cb',
            borderRadius: '4px',
            backgroundColor: '#fff3f3',
            color: '#721c24',
          }}
        >
          <strong>⚠️ {this.props.sectionName}</strong>
          <p style={{ margin: '0.5rem 0 0 0', fontSize: '0.9rem' }}>
            This section encountered an error and could not be displayed.
          </p>
        </div>
      );
    }
    return (
      <div className="report-section" style={{ marginBottom: '1.5rem' }}>
        {!this.props.hideTitle && (
          <h3 style={{ fontSize: '1.1rem', fontWeight: 700, color: '#5A46AA', marginBottom: '0.75rem' }}>
            {this.props.sectionName}
          </h3>
        )}
        {this.props.children}
      </div>
    );
  }
}

// ─── Vitals Chart Data Builder ────────────────────────────────────────────────

import moment from 'moment';

/**
 * Build ChartDataSeries for each vital type from the raw API response.
 * The raw API returns arrays with fields like heart_rate, bp_systolic, bp_distolic, temperature, oxygen.
 */
function buildVitalsChartData(rawResponse: any): {
  heartRate?: ChartDataSeries;
  bloodPressure?: ChartDataSeries;
  temperature?: ChartDataSeries;
  spo2?: ChartDataSeries;
} {
  const result: {
    heartRate?: ChartDataSeries;
    bloodPressure?: ChartDataSeries;
    temperature?: ChartDataSeries;
    spo2?: ChartDataSeries;
  } = {};

  /**
   * Generate 30 date labels (past 30 days) for empty chart skeletons.
   */
  const generate30DayLabels = (): string[] => {
    const labels: string[] = [];
    const today = moment();
    for (let i = 29; i >= 0; i--) {
      labels.push(moment(today).subtract(i, 'days').format('MMM D'));
    }
    return labels;
  };

  // Heart Rate
  const hrRecords = (rawResponse.heartrate || []).filter((r: any) => r.recorded_at);
  if (hrRecords.length > 0) {
    const sorted = [...hrRecords].sort((a: any, b: any) => a.recorded_at.localeCompare(b.recorded_at));
    result.heartRate = {
      labels: sorted.map((r: any) => moment(r.recorded_at).format('MMM D')),
      datasets: [{
        label: 'Heart Rate (bpm)',
        data: sorted.map((r: any) => r.heart_rate ?? 0),
      }],
    };
  } else {
    const labels = generate30DayLabels();
    result.heartRate = {
      labels,
      datasets: [{ label: 'Heart Rate (bpm)', data: labels.map(() => 0) }],
    };
  }

  // Blood Pressure (systolic + diastolic as two datasets)
  const bpRecords = (rawResponse.bloodpressure || []).filter((r: any) => r.recorded_at);
  if (bpRecords.length > 0) {
    const sorted = [...bpRecords].sort((a: any, b: any) => a.recorded_at.localeCompare(b.recorded_at));
    result.bloodPressure = {
      labels: sorted.map((r: any) => moment(r.recorded_at).format('MMM D')),
      datasets: [
        {
          label: 'Systolic',
          data: sorted.map((r: any) => r.bp_systolic ?? 0),
        },
        {
          label: 'Diastolic',
          data: sorted.map((r: any) => r.bp_distolic ?? r.bp_diastolic ?? 0),
        },
      ],
    };
  } else {
    const labels = generate30DayLabels();
    result.bloodPressure = {
      labels,
      datasets: [
        { label: 'Systolic', data: labels.map(() => 0) },
        { label: 'Diastolic', data: labels.map(() => 0) },
      ],
    };
  }

  // Temperature
  const tempRecords = (rawResponse.temperature || []).filter((r: any) => r.recorded_at);
  if (tempRecords.length > 0) {
    const sorted = [...tempRecords].sort((a: any, b: any) => a.recorded_at.localeCompare(b.recorded_at));
    result.temperature = {
      labels: sorted.map((r: any) => moment(r.recorded_at).format('MMM D')),
      datasets: [{
        label: 'Temperature (°C)',
        data: sorted.map((r: any) => r.temperature ?? 0),
      }],
    };
  } else {
    const labels = generate30DayLabels();
    result.temperature = {
      labels,
      datasets: [{ label: 'Temperature (°C)', data: labels.map(() => 0) }],
    };
  }

  // SpO2
  const spo2Records = (rawResponse.spo2 || []).filter((r: any) => r.recorded_at);
  if (spo2Records.length > 0) {
    const sorted = [...spo2Records].sort((a: any, b: any) => a.recorded_at.localeCompare(b.recorded_at));
    result.spo2 = {
      labels: sorted.map((r: any) => moment(r.recorded_at).format('MMM D')),
      datasets: [{
        label: 'SpO2 (%)',
        data: sorted.map((r: any) => r.oxygen ?? 0),
      }],
    };
  } else {
    const labels = generate30DayLabels();
    result.spo2 = {
      labels,
      datasets: [{ label: 'SpO2 (%)', data: labels.map(() => 0) }],
    };
  }

  return result;
}

/**
 * Build vitals table rows by merging all vital types by date.
 * Each row shows: Date, BP, Heart Rate, Temperature, SpO2, Weight
 */
function buildVitalsTable(rawResponse: any, bpClusters: ClusterResult<BpReading>, glucoseClusters: ClusterResult<GlucoseReading>): VitalsTableRow[] {
  // Collect all unique dates from all vital types
  const dateMap = new Map<string, {
    clusterValues: Record<string, string>;
    heartRate: string;
    temperature: string;
    spo2: string;
    weight: string;
  }>();

  const getOrCreate = (dateKey: string) => {
    if (!dateMap.has(dateKey)) {
      dateMap.set(dateKey, { clusterValues: {}, heartRate: '-- bpm', temperature: '-- °C', spo2: '-- %', weight: '-- kg' });
    }
    return dateMap.get(dateKey)!;
  };

  // Heart Rate
  for (const r of (rawResponse.heartrate || [])) {
    if (!r.recorded_at) continue;
    const dateKey = moment(r.recorded_at).format('DD/MM/YYYY');
    const val = r.heart_rate;
    getOrCreate(dateKey).heartRate = val != null ? String(val) : '-- bpm';
  }

  // Temperature
  for (const r of (rawResponse.temperature || [])) {
    if (!r.recorded_at) continue;
    const dateKey = moment(r.recorded_at).format('DD/MM/YYYY');
    const val = r.temperature;
    getOrCreate(dateKey).temperature = val != null ? String(val) : '-- °C';
  }

  // SpO2
  for (const r of (rawResponse.spo2 || [])) {
    if (!r.recorded_at) continue;
    const dateKey = moment(r.recorded_at).format('DD/MM/YYYY');
    const val = r.oxygen;
    getOrCreate(dateKey).spo2 = val != null ? String(val) : '-- %';
  }

  // Weight
  for (const r of (rawResponse.weight || [])) {
    if (!r.recorded_at) continue;
    const dateKey = moment(r.recorded_at).format('DD/MM/YYYY');
    const val = r.weight;
    getOrCreate(dateKey).weight = val != null ? String(val) : '-- kg';
  }

  const addClustered = (recordedAt: string | null, column: string, value: string) => {
    const date = recordedAt ? moment(recordedAt).format('DD/MM/YYYY') : 'Date unavailable';
    const row = getOrCreate(date);
    row.clusterValues[column] = row.clusterValues[column] ? `${row.clusterValues[column]}, ${value}` : value;
  };

  for (const cluster of bpClusters.clusters) {
    for (const reading of cluster.members) {
      addClustered(reading.recordedAt, `BP ${formatReportTimeGroup(cluster.representativeMinutes)} (mmHg)`, `${reading.systolic}/${reading.diastolic}`);
    }
  }
  for (const reading of bpClusters.unscheduled) addClustered(reading.recordedAt, 'BP Unscheduled (mmHg)', `${reading.systolic}/${reading.diastolic}`);
  for (const cluster of glucoseClusters.clusters) {
    for (const reading of cluster.members) {
      addClustered(reading.recordedAt, `Glucose ${formatReportTimeGroup(cluster.representativeMinutes)} (mg/dL)`, String(reading.glucose));
    }
  }
  for (const reading of glucoseClusters.unscheduled) addClustered(reading.recordedAt, 'Glucose Unscheduled (mg/dL)', String(reading.glucose));

  const rows: VitalsTableRow[] = [];
  const sortedDates = Array.from(dateMap.keys()).sort((a, b) => {
    const da = moment(a, 'DD/MM/YYYY');
    const db = moment(b, 'DD/MM/YYYY');
    return da.valueOf() - db.valueOf();
  });

  for (const date of sortedDates) {
    const data = dateMap.get(date)!;
    rows.push({ date, ...data });
  }

  return rows.sort((a, b) => moment(a.date, 'DD/MM/YYYY').valueOf() - moment(b.date, 'DD/MM/YYYY').valueOf());
}

// ─── MonthlyReportPage Props ─────────────────────────────────────────────────

export interface MonthlyReportPageProps {
  customerId: string;
  year: number;
  month: number;
  patientName: string;
}

// ─── MonthlyReportPage Component ─────────────────────────────────────────────

interface VitalAverages {
  bpSystolic: number | null;
  bpDiastolic: number | null;
  heartRate: number | null;
  temperature: number | null;
  spo2: number | null;
  weight: number | null;
}

interface ColorCounts {
  stool: { label: string; count: number }[];
  urine: { label: string; count: number }[];
}

type ReportState =
  | { status: 'loading' }
  | { status: 'error'; message: string }
  | { status: 'empty' }
  | { status: 'ready'; viewModel: MonthlyReportViewModel; vitalsCharts: { heartRate?: ChartDataSeries; bloodPressure?: ChartDataSeries; temperature?: ChartDataSeries; spo2?: ChartDataSeries }; vitalsTable: VitalsTableRow[]; companyInfo?: CompanyInfo; patientInfo?: PatientInfo; qrCodeDataUrl?: string; qrCodeUrl?: string; resolvedPatientName?: string; vitalAverages?: VitalAverages; colorCounts?: ColorCounts; recorderNames?: { name: string; count: number }[] };

const MonthlyReportPage: React.FC<MonthlyReportPageProps> = ({
  customerId,
  year,
  month,
  patientName,
}) => {
  const [reportState, setReportState] = useState<ReportState>({ status: 'loading' });

  useEffect(() => {
    let cancelled = false;

    async function generateReport() {
      setReportState({ status: 'loading' });

      try {
        // 0. Fetch customer info for company details and QR code
        let companyInfo: CompanyInfo | undefined;
        let patientInfo: PatientInfo | undefined;
        let qrCodeDataUrl: string | undefined;
        let qrCodeUrl: string | undefined;
        let resolvedPatientName = patientName;

        try {
          // Cache the customer info for 1 hour to avoid refetching on every
          // report view. Key includes customerId so each patient is isolated.
          const CUSTOMER_CACHE_KEY = `ht_report_customer_${customerId}`;
          const ONE_HOUR_MS = 60 * 60 * 1000;
          let customer: any = null;

          try {
            const cached = localStorage.getItem(CUSTOMER_CACHE_KEY);
            if (cached) {
              const parsed = JSON.parse(cached);
              if (parsed && parsed.cachedAt && Date.now() - parsed.cachedAt < ONE_HOUR_MS && parsed.data) {
                customer = parsed.data;
              }
            }
          } catch { /* ignore parse errors */ }

          if (!customer) {
            const customerResponse = await axios.get(
              `${API_DOMAIN}/careadmin/customers/${customerId}`,
              { headers: { 'x-api-key': API_KEY } }
            );
            const customerData = customerResponse.data;
            customer = Array.isArray(customerData) ? customerData[0] : customerData;

            // Persist to localStorage (silent on failure)
            try {
              localStorage.setItem(CUSTOMER_CACHE_KEY, JSON.stringify({ cachedAt: Date.now(), data: customer }));
            } catch { /* quota/unavailable — ignore */ }
          }

          if (customer) {
            // Resolve patient name
            resolvedPatientName = customer.full_name || `${customer.first_name || ''} ${customer.last_name || ''}`.trim() || patientName;

            // Resolve company from centre
            const centreId = getCustomerCenter(customer);
            const company = (CenterHashCompanyList as any)[centreId] || CompanyHash["1435752V"];
            if (company) {
              companyInfo = {
                companyName: company.companyName || '',
                companyCode: company.companyCode || '',
                companyLogo: company.companyLogo || '',
                companyAddress1: company.companyAddress1 || '',
                companyAddress2: company.companyAddress2 || '',
                companyAddress3: company.companyAddress3 || '',
                telephone: company.telephone || '',
                email: company.email || '',
              };
            }

            // Build patient info
            const identification = customer.ic_no || customer.passport || '-';
            const dob = customer.dob ? moment(customer.dob).format('DD/MM/YYYY') : '-';
            const age = customer.dob ? String(moment().diff(moment(customer.dob), 'years')) + ' years' : '-';
            const gender = customer.sex ? (customer.sex.charAt(0).toUpperCase() + customer.sex.slice(1)) : '-';
            const nationality = customer.nationality ? (customer.nationality.charAt(0).toUpperCase() + customer.nationality.slice(1)) : '-';
            const addressParts = [customer.add_street, customer.add_street2, customer.add_city, customer.add_postcode, customer.add_state, customer.add_country].filter(Boolean);
            const address = addressParts.length > 0 ? addressParts.join(', ') : '-';
            const mobile = customer.mobile || '-';

            // Resolve centre name
            const centreObj = CentreList?.find((c: any) => c.id == customer.centre);
            const centreName = centreObj?.centreName || '-';

            // Resolve care owner: 1st careowner_list, 2nd paymaster, 3rd customer name
            let careOwner = '-';
            if (Array.isArray(customer.careowner_list) && customer.careowner_list.length > 0) {
              const sorted = [...customer.careowner_list].sort((a: any, b: any) => (b.priority || 0) - (a.priority || 0));
              careOwner = sorted[0]?.full_name || sorted[0]?.first_name || sorted[0]?.email || '-';
            } else if (customer.paymaster) {
              careOwner = customer.paymaster;
            } else {
              careOwner = resolvedPatientName || '-';
            }

            patientInfo = {
              name: resolvedPatientName,
              id: String(customer.id || customerId),
              identification,
              dob,
              age,
              gender,
              nationality,
              address,
              mobile,
              careOwner,
              centre: centreName,
            };

            // Generate QR code
            const base = (import.meta as any).env?.VITE_CLIENT_DOMAIN || window.location.origin;
            const encKey = "CareConcierge";
            const encryptedId = encodeURIComponent(CryptoJS.AES.encrypt(String(customerId), encKey).toString());
            qrCodeUrl = `${base}/health_tracker?customerId=${encryptedId}`;
            qrCodeDataUrl = await QRCode.toDataURL(qrCodeUrl, { width: 200, margin: 1 });
          }
        } catch (e) {
          console.warn('[MonthlyReport] Failed to fetch customer info:', e);
          // Continue without company info — not critical
        }

        // 1. Fetch raw journal data
        const rawResponse = await fetchMonthlyJournals({ customerId, year, month });

        // Check if all arrays are empty → "No data available"
        const allEmpty =
          rawResponse.input.length === 0 &&
          rawResponse.output.length === 0 &&
          rawResponse.heartrate.length === 0 &&
          rawResponse.bloodpressure.length === 0 &&
          rawResponse.temperature.length === 0 &&
          rawResponse.spo2.length === 0 &&
          rawResponse.adl.length === 0 &&
          rawResponse.note.length === 0 &&
          rawResponse.weight.length === 0;

        if (allEmpty) {
          if (!cancelled) setReportState({ status: 'empty' });
          return;
        }

        // 2. Extract categories
        const categories = extractCategories(rawResponse);

        // 3. Normalize records
        const normalizedInputs = normalizeInputRecords(categories.input);
        const normalizedOutputs = normalizeOutputRecords(categories.output);
        const normalizedNotes = normalizeNoteRecords(categories.notes);

        // 4. Deduplicate
        const dedupedInputs = deduplicate(normalizedInputs);
        const dedupedOutputs = deduplicate(normalizedOutputs);
        const dedupedNotes = deduplicate(normalizedNotes);

        // 5. Split output records
        const stoolRecords = dedupedOutputs.unique.filter((r) => r.outputCode === 1);
        const urineRecords = dedupedOutputs.unique.filter((r) => r.outputCode === 2);

        // 6. Compute date range for aggregation
        const startDate = `${year}-${String(month).padStart(2, '0')}-01`;
        const lastDay = new Date(year, month, 0).getDate();
        const endDate = `${year}-${String(month).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;

        // 7. Aggregate
        const inputReport = aggregateInput(
          dedupedInputs.unique,
          startDate,
          endDate
        );
        const outputReport = aggregateOutput(
          dedupedOutputs.unique,
          startDate,
          endDate
        );
        const notesReport = aggregateNotes(
          dedupedNotes.unique,
          startDate,
          endDate
        );

        // 8. Build raw events for appendix
        const rawEvents = [
          ...rawResponse.input.map((r) => ({
            id: r.id,
            category: 'input',
            recordedAt: r.recorded_at,
            summary: `Meal type ${r.meal}, feeding type ${r.type}`,
            rawPayload: r as unknown as Record<string, unknown>,
          })),
          ...rawResponse.output.map((r) => ({
            id: r.id,
            category: 'output',
            recordedAt: r.recorded_at,
            summary: `Category ${(r as any).category ?? (r as any).output ?? ''}, type ${r.type}`,
            rawPayload: r as unknown as Record<string, unknown>,
          })),
          ...rawResponse.note.map((r) => ({
            id: r.id,
            category: 'note',
            recordedAt: r.recorded_at,
            summary: (r.note || (r as any).notes || '').substring(0, 80),
            rawPayload: r as unknown as Record<string, unknown>,
          })),
        ].sort((a, b) => a.recordedAt.localeCompare(b.recordedAt));

        const bpReadings: BpReading[] = rawResponse.bloodpressure
          .filter((r: any) => Number.isFinite(Number(r.bp_systolic)) && Number.isFinite(Number(r.bp_distolic ?? r.bp_diastolic)))
          .map((r: any) => ({
            id: r.id,
            recordedAt: r.recorded_at || null,
            systolic: Number(r.bp_systolic),
            diastolic: Number(r.bp_distolic ?? r.bp_diastolic),
            raw: r,
          }));
        const glucoseReadings: GlucoseReading[] = rawResponse.glucose.map((r: any) => ({
          id: r.id,
          recordedAt: r.recorded_at || null,
          glucose: readGlucoseValue(r)!,
          raw: r,
        }));
        const bloodPressureClusters = clusterVitalRecords(bpReadings);
        const glucoseClusters = clusterVitalRecords(glucoseReadings);

        // 9. Build view model
        const viewModel = buildMonthlyReportViewModel({
          patientName: resolvedPatientName || patientName,
          patientId: customerId,
          year,
          month,
          inputReport,
          outputReport,
          notesReport,
          vitalCounts: {
            heartrate: rawResponse.heartrate.length,
            bloodpressure: rawResponse.bloodpressure.length,
            temperature: rawResponse.temperature.length,
            spo2: rawResponse.spo2.length,
          },
          stoolRecords,
          urineRecords,
          rawEvents,
          bloodPressureClusters,
          glucoseClusters,
          glucoseDataPointCount: glucoseReadings.length,
        });

        // 10. Build vitals chart data from raw vital records
        const vitalsCharts = buildVitalsChartData(rawResponse);
        const vitalsTable = buildVitalsTable(rawResponse, bloodPressureClusters, glucoseClusters);

        // 11. Compute vital averages
        const avg = (arr: number[]) => arr.length > 0 ? Math.round(arr.reduce((a, b) => a + b, 0) / arr.length * 10) / 10 : null;
        const bpSysValues = (rawResponse.bloodpressure || []).map((r: any) => r.bp_systolic).filter((v: any) => v != null && !isNaN(v));
        const bpDiaValues = (rawResponse.bloodpressure || []).map((r: any) => r.bp_distolic ?? r.bp_diastolic).filter((v: any) => v != null && !isNaN(v));
        const hrValues = (rawResponse.heartrate || []).map((r: any) => r.heart_rate).filter((v: any) => v != null && !isNaN(v));
        const tempValues = (rawResponse.temperature || []).map((r: any) => r.temperature).filter((v: any) => v != null && !isNaN(v));
        const spo2Values = (rawResponse.spo2 || []).map((r: any) => r.oxygen).filter((v: any) => v != null && !isNaN(v));
        const weightValues = (rawResponse.weight || []).map((r: any) => r.weight).filter((v: any) => v != null && !isNaN(v));

        const vitalAverages: VitalAverages = {
          bpSystolic: avg(bpSysValues),
          bpDiastolic: avg(bpDiaValues),
          heartRate: avg(hrValues),
          temperature: avg(tempValues),
          spo2: avg(spo2Values),
          weight: avg(weightValues),
        };

        // 12. Compute stool & urine color count totals
        const stoolColorMap = new Map<string, number>();
        for (const r of stoolRecords) {
          const label = r.colorLabel || 'Unknown';
          stoolColorMap.set(label, (stoolColorMap.get(label) || 0) + 1);
        }
        const urineColorMap = new Map<string, number>();
        for (const r of urineRecords) {
          const label = r.colorLabel || 'Unknown';
          urineColorMap.set(label, (urineColorMap.get(label) || 0) + 1);
        }
        const colorCounts: ColorCounts = {
          stool: Array.from(stoolColorMap.entries()).map(([label, count]) => ({ label, count })).sort((a, b) => b.count - a.count),
          urine: Array.from(urineColorMap.entries()).map(([label, count]) => ({ label, count })).sort((a, b) => b.count - a.count),
        };

        // 13. Extract unique recorder names with record counts from all raw records
        const nameCountMap = new Map<string, number>();
        const allRecordArrays = [
          rawResponse.input, rawResponse.output, rawResponse.heartrate,
          rawResponse.bloodpressure, rawResponse.temperature, rawResponse.spo2,
          rawResponse.note, rawResponse.adl, rawResponse.weight || [],
        ];
        for (const arr of allRecordArrays) {
          if (!Array.isArray(arr)) continue;
          for (const record of arr) {
            let full = '';
            // Try creator.first_name + creator.last_name
            if (record?.creator) {
              const first = (record.creator.first_name || '').trim();
              const last = (record.creator.last_name || '').trim();
              full = [first, last].filter(Boolean).join(' ').trim();
            } else if (record?.creator_name) {
              full = record.creator_name.trim();
            }
            if (full) {
              nameCountMap.set(full, (nameCountMap.get(full) || 0) + 1);
            }
          }
        }
        const recorderNames = Array.from(nameCountMap.entries())
          .map(([name, count]) => ({ name, count }))
          .sort((a, b) => b.count - a.count);

        if (!cancelled) {
          setReportState({ status: 'ready', viewModel, vitalsCharts, vitalsTable, companyInfo, patientInfo, qrCodeDataUrl, qrCodeUrl, resolvedPatientName, vitalAverages, colorCounts, recorderNames });
        }
      } catch (err) {
        if (!cancelled) {
          const message =
            err instanceof Error ? err.message : 'An unexpected error occurred';
          setReportState({ status: 'error', message });
        }
      }
    }

    generateReport();

    return () => {
      cancelled = true;
    };
  }, [customerId, year, month, patientName]);

  // ─── Render States ───────────────────────────────────────────────────────────

  if (reportState.status === 'loading') {
    return (
      <div className="monthly-report-loading" style={{ textAlign: 'center', padding: '3rem' }}>
        <div className="spinner" style={{ marginBottom: '1rem' }}>
          <div
            style={{
              width: '40px',
              height: '40px',
              margin: '0 auto',
              border: '4px solid #f3f3f3',
              borderTop: '4px solid #3498db',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
            }}
          />
        </div>
        <p>Generating monthly report...</p>
        <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  if (reportState.status === 'error') {
    return (
      <div
        className="monthly-report-error"
        style={{
          padding: '2rem',
          textAlign: 'center',
          color: '#721c24',
          backgroundColor: '#f8d7da',
          border: '1px solid #f5c6cb',
          borderRadius: '4px',
          margin: '1rem',
        }}
      >
        <h3 style={{ margin: '0 0 0.5rem 0' }}>Failed to generate report</h3>
        <p style={{ margin: 0 }}>{reportState.message}</p>
      </div>
    );
  }

  if (reportState.status === 'empty') {
    return (
      <div
        className="monthly-report-empty"
        style={{
          padding: '2rem',
          textAlign: 'center',
          color: '#856404',
          backgroundColor: '#fff3cd',
          border: '1px solid #ffc107',
          borderRadius: '4px',
          margin: '1rem',
        }}
      >
        <p style={{ margin: 0 }}>No data available for this month.</p>
      </div>
    );
  }

  // ─── Ready State: Render Report Sections ─────────────────────────────────────

  const { viewModel, vitalsCharts, vitalsTable, companyInfo, patientInfo, qrCodeDataUrl, qrCodeUrl, resolvedPatientName, vitalAverages, colorCounts, recorderNames } = reportState;

  // Override patient name in header if resolved from API
  const headerWithName = {
    ...viewModel.header,
    patientName: resolvedPatientName || viewModel.header.patientName,
  };

  return (
    <div className="monthly-report-page">
      <SectionErrorBoundary sectionName="Report Header" hideTitle>
        <ReportHeader
          header={headerWithName}
          companyInfo={companyInfo}
          patientInfo={patientInfo}
          qrCodeDataUrl={qrCodeDataUrl}
          qrCodeUrl={qrCodeUrl}
          recorderNames={recorderNames}
        />
      </SectionErrorBoundary>

      <SectionErrorBoundary sectionName="Executive Summary">
        <ExecutiveSummary summary={viewModel.executiveSummary} />
      </SectionErrorBoundary>
      <SectionErrorBoundary sectionName="">
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px', marginBottom: '1.5rem' }}>
          {/* Average Vitals */}
          {vitalAverages && (
            <div style={{ border: '1px solid #f0f0f0', borderRadius: '10px', overflow: 'hidden', backgroundColor: '#fff' }}>
              <div style={{ padding: '10px 14px', backgroundColor: '#f8f9fa', borderBottom: '1px solid #f0f0f0' }}>
                <span style={{ fontSize: '0.78rem', fontWeight: 700, color: '#555', textTransform: 'uppercase', letterSpacing: '0.4px' }}>Average Monthly Vitals</span>
              </div>
              <div style={{ padding: '6px 0' }}>
                {[
                  { label: 'BP (mmHg)', value: vitalAverages.bpSystolic != null ? `${vitalAverages.bpSystolic}/${vitalAverages.bpDiastolic}` : '--/--' },
                  { label: 'Heart Rate (bpm)', value: vitalAverages.heartRate ?? '--' },
                  { label: 'Temp (°C)', value: vitalAverages.temperature ?? '--' },
                  { label: 'SpO2 (%)', value: vitalAverages.spo2 ?? '--' },
                  { label: 'Weight (kg)', value: vitalAverages.weight ?? '--' },
                ].map((row) => (
                  <div key={row.label} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 14px' }}>
                    <span style={{ fontSize: '0.88rem', color: '#444' }}>{row.label}</span>
                    <span style={{ fontSize: '0.88rem', fontWeight: 700, color: '#2c3e50' }}>{row.value}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Urine Type */}
          <div style={{ border: '1px solid #f0f0f0', borderRadius: '10px', overflow: 'hidden', backgroundColor: '#fff' }}>
            <div style={{ padding: '10px 14px', backgroundColor: '#f8f9fa', borderBottom: '1px solid #f0f0f0' }}>
              <span style={{ fontSize: '0.78rem', fontWeight: 700, color: '#555', textTransform: 'uppercase', letterSpacing: '0.4px' }}>Methods of Urinary</span>
            </div>
            <div style={{ padding: '6px 0', display: 'flex', flexDirection: 'column' }}>
              <div style={{ flex: 1 }}>
                {viewModel.outputCharts.urineTypeHistogram.labels.map((label, idx) => {
                  const count = viewModel.outputCharts.urineTypeHistogram.data[idx];
                  const isZero = count === 0;
                  return (
                    <div key={label} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 14px' }}>
                      <span style={{ fontSize: '0.88rem', color: isZero ? '#AAAAAA' : '#444' }}>{label}</span>
                      <span style={{ fontSize: '0.88rem', fontWeight: 700, color: isZero ? '#AAAAAA' : '#2c3e50' }}>{count}</span>
                    </div>
                  );
                })}
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 14px', borderTop: '1px solid #eee', marginTop: '4px' }}>
                <span style={{ fontSize: '0.88rem', fontWeight: 700, color: '#333' }}>Total</span>
                <span style={{ fontSize: '0.88rem', fontWeight: 700, color: '#2c3e50' }}>{viewModel.outputCharts.urineTypeHistogram.data.reduce((a, b) => a + b, 0)}</span>
              </div>
            </div>
          </div>

          {/* Stool & Urine Color Count */}
          {colorCounts && (
            <div style={{ border: '1px solid #f0f0f0', borderRadius: '10px', overflow: 'hidden', backgroundColor: '#fff' }}>
              <div style={{ padding: '10px 14px', backgroundColor: '#f8f9fa', borderBottom: '1px solid #f0f0f0' }}>
                <span style={{ fontSize: '0.78rem', fontWeight: 700, color: '#555', textTransform: 'uppercase', letterSpacing: '0.4px' }}>Elimination Color</span>
              </div>
              <div style={{ padding: '6px 0' }}>
                {colorCounts.stool.length > 0 && (
                  <>
                    <div style={{ padding: '4px 14px 2px', fontSize: '0.72rem', fontWeight: 700, color: '#888', textTransform: 'uppercase', letterSpacing: '0.3px' }}>Stool</div>
                    {colorCounts.stool.map((item) => (
                      <div key={`s-${item.label}`} style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 14px' }}>
                        <span style={{ fontSize: '0.85rem', color: '#444' }}>{item.label}</span>
                        <span style={{ fontSize: '0.85rem', fontWeight: 700, color: '#2c3e50' }}>{item.count}</span>
                      </div>
                    ))}
                  </>
                )}
                {colorCounts.urine.length > 0 && (
                  <>
                    <div style={{ padding: '8px 14px 2px', fontSize: '0.72rem', fontWeight: 700, color: '#888', textTransform: 'uppercase', letterSpacing: '0.3px', borderTop: colorCounts.stool.length > 0 ? '1px solid #f0f0f0' : 'none', marginTop: colorCounts.stool.length > 0 ? '4px' : '0' }}>Urine</div>
                    {colorCounts.urine.map((item) => (
                      <div key={`u-${item.label}`} style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 14px' }}>
                        <span style={{ fontSize: '0.85rem', color: '#444' }}>{item.label}</span>
                        <span style={{ fontSize: '0.85rem', fontWeight: 700, color: '#2c3e50' }}>{item.count}</span>
                      </div>
                    ))}
                  </>
                )}
              </div>
            </div>
          )}
          
          {/* Feeding Method */}
          <div style={{ border: '1px solid #f0f0f0', borderRadius: '10px', overflow: 'hidden', backgroundColor: '#fff' }}>
            <div style={{ padding: '10px 14px', backgroundColor: '#f8f9fa', borderBottom: '1px solid #f0f0f0' }}>
              <span style={{ fontSize: '0.78rem', fontWeight: 700, color: '#555', textTransform: 'uppercase', letterSpacing: '0.4px' }}>Feeding Method</span>
            </div>
            <div style={{ padding: '6px 0', display: 'flex', flexDirection: 'column' }}>
              <div style={{ flex: 1 }}>
                {[
                  { label: 'Normal', value: viewModel.feedingSummary.normalCount },
                  { label: 'NGT', value: viewModel.feedingSummary.ngtCount },
                  { label: 'Peg Tube', value: viewModel.feedingSummary.pegTubeCount },
                ].map((row) => {
                  const isZero = row.value === 0;
                  return (
                    <div key={row.label} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 14px' }}>
                      <span style={{ fontSize: '0.88rem', color: isZero ? '#AAAAAA' : '#444' }}>{row.label}</span>
                      <span style={{ fontSize: '0.88rem', fontWeight: 700, color: isZero ? '#AAAAAA' : '#2c3e50' }}>{row.value}</span>
                    </div>
                  );
                })}
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 14px', borderTop: '1px solid #eee', marginTop: '4px' }}>
                <span style={{ fontSize: '0.88rem', fontWeight: 700, color: '#333' }}>Total</span>
                <span style={{ fontSize: '0.88rem', fontWeight: 700, color: '#2c3e50' }}>{viewModel.feedingSummary.normalCount + viewModel.feedingSummary.ngtCount + viewModel.feedingSummary.pegTubeCount}</span>
              </div>
            </div>
          </div>
        </div>
      </SectionErrorBoundary>

      <SectionErrorBoundary sectionName="Vitals Graphs">
        <VitalsGraphs
          heartRate={vitalsCharts.heartRate}
          temperature={vitalsCharts.temperature}
          spo2={vitalsCharts.spo2}
          vitalsTable={vitalsTable}
          clusteredVitals={viewModel.clusteredVitals}
        />
      </SectionErrorBoundary>

      <SectionErrorBoundary sectionName="Elimination/Output Analytics">
        <OutputAnalytics charts={viewModel.outputCharts} />
      </SectionErrorBoundary>

      <SectionErrorBoundary sectionName="Care Notes Timeline">
        <CareNotesTimeline notes={viewModel.notesTimeline} />
      </SectionErrorBoundary>

      <SectionErrorBoundary sectionName="Input Meal Matrix">
        <InputMealMatrix mealMatrix={viewModel.mealMatrix} />
      </SectionErrorBoundary>

      <SectionErrorBoundary sectionName="Raw Event Appendix">
        <RawEventAppendix events={viewModel.rawEvents} />
      </SectionErrorBoundary>
    </div>
  );
};

export default MonthlyReportPage;
